import { Form, Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState, useCallback } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  FormLayout,
  Checkbox,
  Banner,
  Tag,
  Collapsible,
  Divider,
  Spinner,
  Badge,
} from '@shopify/polaris';
import { DeleteIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon, ImportIcon } from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

// ============================================================
// TYPES
// ============================================================

interface ZonePostcode {
  id: number;
  postcode: string;
  is_pattern: number;
}

interface ZoneShippingProfile {
  id: number;
  name: string;
  shopify_rate_id: string | null;
  shopify_rate_handle: string | null;
  lead_time_days: number | null;
  cutoff_time: string | null;
  available_days: string[] | null;
  surcharge_amount: number;
  is_active: number;
}

interface DeliveryZone {
  id: number;
  name: string;
  code: string | null;
  region: string | null;
  lead_time_days: number;
  cutoff_time: string;
  available_days: string[];
  surcharge_amount: number;
  surcharge_reason: string | null;
  supports_pickup: number;
  pickup_location_id: number | null;
  is_active: number;
  priority: number;
  postcodes: ZonePostcode[];
  shipping_profiles: ZoneShippingProfile[];
}

const DAYS_OF_WEEK = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];

const REGIONS = ['Central', 'North', 'South', 'East', 'West', 'Gozo'];

// ============================================================
// COMPONENT
// ============================================================

export default function DeliveryZones() {
  const { fetchApi, error: apiError } = useApi();

  // State
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedZones, setExpandedZones] = useState<Set<number>>(new Set());

  // New postcode input per zone
  const [postcodeInputs, setPostcodeInputs] = useState<Record<number, string>>({});
  // Inline error per zone (e.g. duplicate)
  const [postcodeErrors, setPostcodeErrors] = useState<Record<number, string | null>>({});
  // Editing a postcode inline
  const [editingPostcode, setEditingPostcode] = useState<{ zoneId: number; postcodeId: number; value: string } | null>(null);

  // Zone modal
  const [zoneModalActive, setZoneModalActive] = useState(false);
  const [editingZone, setEditingZone] = useState<DeliveryZone | null>(null);
  const [zoneName, setZoneName] = useState('');
  const [zoneCode, setZoneCode] = useState('');
  const [zoneRegion, setZoneRegion] = useState('');
  const [zoneLeadTime, setZoneLeadTime] = useState('1');
  const [zoneCutoff, setZoneCutoff] = useState('14:00');
  const [zoneDays, setZoneDays] = useState<string[]>(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [zoneSurcharge, setZoneSurcharge] = useState('0');
  const [zoneSurchargeReason, setZoneSurchargeReason] = useState('');
  const [zoneSupportsPickup, setZoneSupportsPickup] = useState(false);
  const [zonePriority, setZonePriority] = useState('0');

  // Bulk import modal
  const [bulkImportModalActive, setBulkImportModalActive] = useState(false);
  const [bulkImportZoneId, setBulkImportZoneId] = useState<number | null>(null);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportReplace, setBulkImportReplace] = useState(false);

  // ============================================================
  // DATA LOADING
  // ============================================================

  const loadZones = useCallback(async () => {
    setLoading(true);
    setError(null);

    const data = await fetchApi<{ success: boolean; zones: DeliveryZone[]; error?: string }>('/zones');

    if (data?.success) {
      setZones(data.zones || []);
    } else {
      setError(data?.error || 'Failed to load zones');
    }

    setLoading(false);
  }, [fetchApi]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  // ============================================================
  // ZONE CRUD
  // ============================================================

  const openAddZoneModal = () => {
    setEditingZone(null);
    setZoneName('');
    setZoneCode('');
    setZoneRegion('');
    setZoneLeadTime('1');
    setZoneCutoff('14:00');
    setZoneDays(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
    setZoneSurcharge('0');
    setZoneSurchargeReason('');
    setZoneSupportsPickup(false);
    setZonePriority('0');
    setZoneModalActive(true);
  };

  const openEditZoneModal = (zone: DeliveryZone) => {
    setEditingZone(zone);
    setZoneName(zone.name);
    setZoneCode(zone.code || '');
    setZoneRegion(zone.region || '');
    setZoneLeadTime(String(zone.lead_time_days));
    setZoneCutoff(zone.cutoff_time);
    setZoneDays(zone.available_days || []);
    setZoneSurcharge(String(zone.surcharge_amount || 0));
    setZoneSurchargeReason(zone.surcharge_reason || '');
    setZoneSupportsPickup(zone.supports_pickup === 1);
    setZonePriority(String(zone.priority));
    setZoneModalActive(true);
  };

  const saveZone = async () => {
    setSaving(true);
    setError(null);

    const zoneData = {
      name: zoneName,
      code: zoneCode || null,
      region: zoneRegion || null,
      lead_time_days: parseInt(zoneLeadTime, 10),
      cutoff_time: zoneCutoff,
      available_days: zoneDays,
      surcharge_amount: parseFloat(zoneSurcharge) || 0,
      surcharge_reason: zoneSurchargeReason || null,
      supports_pickup: zoneSupportsPickup,
      priority: parseInt(zonePriority, 10),
    };

    let result;
    if (editingZone) {
      result = await fetchApi<{ success: boolean; zone?: DeliveryZone; error?: string }>(`/zones/${editingZone.id}`, {
        method: 'PUT',
        body: JSON.stringify(zoneData),
      });
    } else {
      result = await fetchApi<{ success: boolean; zone?: DeliveryZone; error?: string }>('/zones', {
        method: 'POST',
        body: JSON.stringify(zoneData),
      });
    }

    if (result?.success) {
      setZoneModalActive(false);
      setSuccessMessage(editingZone ? 'Zone updated successfully' : 'Zone created successfully');
      loadZones();
    } else {
      setError(result?.error || 'Failed to save zone');
    }

    setSaving(false);
  };

  const deleteZone = async (zoneId: number) => {
    if (!confirm('Are you sure you want to delete this zone? All postcodes will be removed.')) {
      return;
    }

    const result = await fetchApi<{ success: boolean; error?: string }>(`/zones/${zoneId}`, {
      method: 'DELETE',
    });

    if (result?.success) {
      setSuccessMessage('Zone deleted');
      loadZones();
    } else {
      setError(result?.error || 'Failed to delete zone');
    }
  };

  // ============================================================
  // POSTCODE MANAGEMENT
  // ============================================================

  const addPostcode = async (zoneId: number) => {
    const postcode = postcodeInputs[zoneId]?.trim();
    if (!postcode) return;

    setPostcodeErrors({ ...postcodeErrors, [zoneId]: null });

    const result = await fetchApi<{ success: boolean; error?: string }>(`/zones/${zoneId}/postcodes`, {
      method: 'POST',
      body: JSON.stringify({ postcodes: [postcode] }),
    });

    if (result?.success) {
      setPostcodeInputs({ ...postcodeInputs, [zoneId]: '' });
      loadZones();
    } else {
      // Show inline error on the zone's postcode input
      setPostcodeErrors({ ...postcodeErrors, [zoneId]: result?.error || 'Failed to add postcode' });
    }
  };

  const saveEditedPostcode = async () => {
    if (!editingPostcode) return;
    const { zoneId, postcodeId, value } = editingPostcode;
    const trimmed = value.trim();
    if (!trimmed) return;

    const result = await fetchApi<{ success: boolean; error?: string }>(
      `/zones/${zoneId}/postcodes/${postcodeId}`,
      { method: 'PUT', body: JSON.stringify({ postcode: trimmed }) }
    );

    if (result?.success) {
      setEditingPostcode(null);
      loadZones();
    } else {
      setError(result?.error || 'Failed to edit postcode');
    }
  };

  const removePostcode = async (zoneId: number, postcodeId: number) => {
    const result = await fetchApi<{ success: boolean; error?: string }>(`/zones/${zoneId}/postcodes/${postcodeId}`, {
      method: 'DELETE',
    });

    if (result?.success) {
      loadZones();
    } else {
      setError(result?.error || 'Failed to remove postcode');
    }
  };

  const openBulkImportModal = (zoneId: number) => {
    setBulkImportZoneId(zoneId);
    setBulkImportText('');
    setBulkImportReplace(false);
    setBulkImportModalActive(true);
  };

  const handleBulkImport = async () => {
    if (!bulkImportZoneId || !bulkImportText.trim()) return;

    setSaving(true);

    const result = await fetchApi<{ success: boolean; stats?: { inserted: number; skipped: number }; error?: string }>(
      `/zones/${bulkImportZoneId}/postcodes/bulk`,
      {
        method: 'POST',
        body: JSON.stringify({
          postcodes: bulkImportText,
          replace: bulkImportReplace,
        }),
      }
    );

    if (result?.success) {
      setBulkImportModalActive(false);
      setSuccessMessage(`Imported ${result.stats?.inserted || 0} postcodes (${result.stats?.skipped || 0} skipped)`);
      loadZones();
    } else {
      setError(result?.error || 'Failed to import postcodes');
    }

    setSaving(false);
  };

  // ============================================================
  // UI HELPERS
  // ============================================================

  const toggleZoneExpanded = (zoneId: number) => {
    setExpandedZones(prev => {
      const next = new Set(prev);
      if (next.has(zoneId)) {
        next.delete(zoneId);
      } else {
        next.add(zoneId);
      }
      return next;
    });
  };

  const toggleDay = (day: string) => {
    setZoneDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  // ============================================================
  // RENDER ZONE CARD
  // ============================================================

  const renderZoneCard = (zone: DeliveryZone) => {
    const isExpanded = expandedZones.has(zone.id);
    const postcodeCount = zone.postcodes?.length || 0;

    return (
      <Card key={zone.id}>
        <BlockStack gap="400">
          {/* Zone Header */}
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="300" blockAlign="center">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="h3" variant="headingMd">{zone.name}</Text>
                  {zone.code && <Badge>{zone.code}</Badge>}
                  {zone.region && <Badge tone="info">{zone.region}</Badge>}
                  {zone.is_active === 0 && <Badge tone="warning">Inactive</Badge>}
                </InlineStack>
                <Text as="span" variant="bodySm" tone="subdued">
                  {postcodeCount} postcode{postcodeCount !== 1 ? 's' : ''} | {zone.lead_time_days} day lead time | Cutoff {zone.cutoff_time}
                </Text>
              </BlockStack>
            </InlineStack>

            <InlineStack gap="200">
              <Button onClick={() => openEditZoneModal(zone)}>Edit</Button>
              <Button
                icon={DeleteIcon}
                tone="critical"
                variant="plain"
                onClick={() => deleteZone(zone.id)}
                accessibilityLabel="Delete zone"
              />
            </InlineStack>
          </InlineStack>

          <Divider />

          {/* Postcodes Section — always visible */}
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h4" variant="headingSm">Postcodes</Text>
              <Button
                icon={ImportIcon}
                variant="plain"
                onClick={() => openBulkImportModal(zone.id)}
              >
                Bulk Import
              </Button>
            </InlineStack>

            {/* Add postcode */}
            <Form onSubmit={(e) => { e.preventDefault(); addPostcode(zone.id); }}>
              <InlineStack gap="200">
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Add postcode"
                    labelHidden
                    value={postcodeInputs[zone.id] || ''}
                    onChange={(value) => {
                      setPostcodeInputs({ ...postcodeInputs, [zone.id]: value });
                      setPostcodeErrors({ ...postcodeErrors, [zone.id]: null });
                    }}
                    placeholder="e.g., VLT1234 or VLT* for pattern"
                    autoComplete="off"
                    error={postcodeErrors[zone.id] || undefined}
                  />
                </div>
                <Button icon={PlusIcon} onClick={() => addPostcode(zone.id)}>Add</Button>
              </InlineStack>
            </Form>

            {/* Postcode tags (with inline edit) */}
            {zone.postcodes && zone.postcodes.length > 0 ? (
              <InlineStack gap="200" wrap>
                {zone.postcodes.map(pc => {
                  // If this postcode is being edited, show inline TextField
                  if (editingPostcode && editingPostcode.zoneId === zone.id && editingPostcode.postcodeId === pc.id) {
                    return (
                      <InlineStack key={pc.id} gap="100" blockAlign="center">
                        <div style={{ width: 120 }}>
                          <TextField
                            label="Edit postcode"
                            labelHidden
                            value={editingPostcode.value}
                            onChange={(value) => setEditingPostcode({ ...editingPostcode, value })}
                            autoComplete="off"
                            autoFocus
                            onBlur={() => {
                              // Small delay to allow Save click to fire
                              setTimeout(() => {
                                setEditingPostcode(prev =>
                                  prev?.postcodeId === pc.id ? null : prev
                                );
                              }, 200);
                            }}
                          />
                        </div>
                        <Button size="slim" onClick={saveEditedPostcode}>Save</Button>
                        <Button size="slim" variant="plain" onClick={() => setEditingPostcode(null)}>Cancel</Button>
                      </InlineStack>
                    );
                  }

                  return (
                    <span
                      key={pc.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setEditingPostcode({ zoneId: zone.id, postcodeId: pc.id, value: pc.postcode })}
                      title="Click to edit"
                    >
                      <Tag onRemove={() => removePostcode(zone.id, pc.id)}>
                        {pc.postcode}{pc.is_pattern ? ' (pattern)' : ''}
                      </Tag>
                    </span>
                  );
                })}
              </InlineStack>
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                No postcodes configured. Add postcodes or use bulk import.
              </Text>
            )}
          </BlockStack>

          {/* Delivery Settings (Collapsible) */}
          <Divider />
          <Button
            variant="plain"
            icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => toggleZoneExpanded(zone.id)}
          >
            {isExpanded ? 'Hide Settings' : 'Show Settings'}
          </Button>
          <Collapsible open={isExpanded} id={`zone-${zone.id}`}>
            <BlockStack gap="200">
              <Text as="h4" variant="headingSm">Delivery Settings</Text>
              <InlineStack gap="400">
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">Lead Time</Text>
                  <Text as="span" variant="bodyMd">{zone.lead_time_days} day{zone.lead_time_days !== 1 ? 's' : ''}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">Cutoff</Text>
                  <Text as="span" variant="bodyMd">{zone.cutoff_time}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text as="span" variant="bodySm" tone="subdued">Available Days</Text>
                  <Text as="span" variant="bodyMd">
                    {zone.available_days?.map(d => d.slice(0, 3)).join(', ') || 'None'}
                  </Text>
                </BlockStack>
                {zone.surcharge_amount > 0 && (
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm" tone="subdued">Surcharge</Text>
                    <Text as="span" variant="bodyMd">
                      +EUR{zone.surcharge_amount.toFixed(2)}
                      {zone.surcharge_reason && ` (${zone.surcharge_reason})`}
                    </Text>
                  </BlockStack>
                )}
              </InlineStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (loading) {
    return (
      <Page title="Delivery Zones">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400" inlineAlign="center">
                <Spinner size="large" />
                <Text as="p">Loading zones...</Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Delivery Zones"
      subtitle="Configure delivery zones with postcodes to control delivery dates and lead times"
      primaryAction={{
        content: 'Add Zone',
        icon: PlusIcon,
        onAction: openAddZoneModal,
      }}
    >
      <Layout>
        {/* Messages */}
        {(error || apiError) && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error || apiError}
            </Banner>
          </Layout.Section>
        )}

        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
              {successMessage}
            </Banner>
          </Layout.Section>
        )}

        {/* Zones List */}
        <Layout.Section>
          {zones.length > 0 ? (
            <BlockStack gap="400">
              {zones.map(zone => renderZoneCard(zone))}
            </BlockStack>
          ) : (
            <Card>
              <BlockStack gap="400" inlineAlign="center">
                <Text as="p" tone="subdued">
                  No delivery zones configured yet. Create your first zone to start managing delivery dates by location.
                </Text>
                <Button icon={PlusIcon} variant="primary" onClick={openAddZoneModal}>
                  Create First Zone
                </Button>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        {/* Help Card */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">How Zones Work</Text>
              <Text as="p" variant="bodySm">
                Zones determine delivery settings based on customer postcode:
              </Text>
              <BlockStack gap="200">
                <Text as="p" variant="bodySm">
                  <strong>Lead Time:</strong> Minimum days before delivery
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Cutoff:</strong> Time after which orders move to next day
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Postcodes:</strong> Exact matches or patterns (use * for wildcard, e.g., VLT*)
                </Text>
                <Text as="p" variant="bodySm">
                  <strong>Priority:</strong> Higher numbers are checked first for overlapping postcodes (for example, 10 before 1). Exact postcode matches take precedence over patterns. Lower numbers do not mean higher importance.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add/Edit Zone Modal */}
      <Modal
        open={zoneModalActive}
        onClose={() => setZoneModalActive(false)}
        title={editingZone ? 'Edit Zone' : 'Create Zone'}
        primaryAction={{
          icon: editingZone ? undefined : PlusIcon, content: 'Save',
          onAction: saveZone,
          loading: saving,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setZoneModalActive(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <details>
              <summary>How zones work</summary>
              <BlockStack gap="200">
                <p>Zones choose delivery settings from the customer's postcode.</p>
                <p><strong>Lead time:</strong> Minimum days before delivery.</p>
                <p><strong>Cutoff:</strong> Orders placed after this time move to the next day.</p>
                <p><strong>Postcodes:</strong> Add exact matches or use * for a pattern, such as VLT*. Add postcodes after saving the zone.</p>
                <p><strong>Priority:</strong> Higher numbers are checked first, for example 10 before 1. Exact postcode matches take precedence over patterns.</p>
              </BlockStack>
            </details>
          <FormLayout>
            <FormLayout.Group>
              <TextField
                label="Zone Name"
                requiredIndicator value={zoneName}
                onChange={setZoneName}
                placeholder="e.g., Zone 1, Central Malta"
                autoComplete="off"

              />
              <TextField
                label="Zone Code"
                requiredIndicator value={zoneCode}
                onChange={setZoneCode}
                placeholder="e.g., Z1, CENTRAL"
                helpText="Short code for order tags"
                autoComplete="off"
              />
            </FormLayout.Group>

            <Select
              label="Region"
              options={[
                { label: 'No region', value: '' },
                ...REGIONS.map(r => ({ label: r, value: r })),
              ]}
              value={zoneRegion}
              onChange={setZoneRegion}
              helpText="Used for order tagging and filtering"
            />

            <FormLayout.Group>
              <TextField
                label="Lead Time (days)"
                type="number"
                value={zoneLeadTime}
                onChange={setZoneLeadTime}
                min={0}
                autoComplete="off"
              />
              <TextField
                label="Cutoff Time"
                type="time"
                value={zoneCutoff}
                onChange={setZoneCutoff}
                autoComplete="off"
              />
            </FormLayout.Group>

            <BlockStack gap="200">
              <Text as="span" variant="bodyMd">Available Delivery Days</Text>
              <InlineStack gap="200" wrap>
                {DAYS_OF_WEEK.map(day => (
                  <Button
                    key={day.value}
                    pressed={zoneDays.includes(day.value)}
                    onClick={() => toggleDay(day.value)}
                    size="slim"
                  >
                    {day.label.slice(0, 3)}
                  </Button>
                ))}
              </InlineStack>
            </BlockStack>

            <Divider />

            <FormLayout.Group>
              <TextField
                label="Surcharge Amount"
                type="number"
                value={zoneSurcharge}
                onChange={setZoneSurcharge}
                prefix="EUR"
                min={0}
                step={0.01}
                autoComplete="off"
              />
              <TextField
                label="Surcharge Reason"
                value={zoneSurchargeReason}
                onChange={setZoneSurchargeReason}
                placeholder="e.g., Remote area fee"
                autoComplete="off"
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <TextField
                label="Priority"
                type="number"
                value={zonePriority}
                onChange={setZonePriority}
                min={0}
                helpText="Higher numbers first: 10 is checked before 1. Exact postcode matches take precedence over patterns."
                autoComplete="off"
              />
              <Checkbox
                label="Supports pickup"
                checked={zoneSupportsPickup}
                onChange={setZoneSupportsPickup}
                helpText="Allow customers to collect orders"
              />
            </FormLayout.Group>
          </FormLayout>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        open={bulkImportModalActive}
        onClose={() => setBulkImportModalActive(false)}
        title="Bulk Import Postcodes"
        primaryAction={{
          content: 'Import',
          onAction: handleBulkImport,
          loading: saving,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setBulkImportModalActive(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Postcodes"
              value={bulkImportText}
              onChange={setBulkImportText}
              multiline={8}
              placeholder="Enter postcodes separated by commas or new lines:&#10;VLT1234&#10;MST1000&#10;BKR*"
              autoComplete="off"
              helpText="Use * for pattern matching (e.g., VLT* matches all VLT postcodes)"
            />

            <Checkbox
              label="Replace existing postcodes"
              checked={bulkImportReplace}
              onChange={setBulkImportReplace}
              helpText="If checked, all existing postcodes for this zone will be removed first"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
